import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Clock } from 'lucide-react'

interface Props {
  daysLeft: number | null
}

const DISMISS_KEY = 'trial_banner_dismissed_until'

export default function TrialBanner({ daysLeft }: Props) {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() => {
    const until = localStorage.getItem(DISMISS_KEY)
    return until ? new Date(until) > new Date() : false
  })

  if (daysLeft === null || dismissed) return null

  // Hard modal at 0 days — don't allow dismissal
  const isExpired = daysLeft <= 0
  const isUrgent = daysLeft <= 5

  if (isExpired) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#FFEBE6' }}>
            <Clock size={28} color="#D93025" />
          </div>
          <h2 className="font-extrabold text-xl mb-2" style={{ color: 'var(--text)' }}>Trial Expired</h2>
          <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
            Your free trial has ended. Upgrade to continue accessing HSEQ Intelligence.
          </p>
          <button
            onClick={() => navigate('/settings/billing')}
            className="w-full py-3 rounded-xl text-white font-bold text-base"
            style={{ background: '#1B2B4B' }}
          >
            Choose a Plan
          </button>
        </div>
      </div>
    )
  }

  const handleDismiss = () => {
    // Persist 5 days
    const until = new Date(Date.now() + 5 * 86400000).toISOString()
    localStorage.setItem(DISMISS_KEY, until)
    setDismissed(true)
  }

  return (
    <div
      className="flex items-center justify-between px-4 py-2 text-sm font-medium"
      style={{ background: isUrgent ? '#FFEBE6' : '#FEF3E2', color: isUrgent ? '#D93025' : '#8A4E00', borderBottom: `1px solid ${isUrgent ? '#D93025' : '#F5921E'}33` }}
    >
      <div className="flex items-center gap-2">
        <Clock size={14} />
        <span>
          {isUrgent
            ? `Trial ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'} — upgrade to keep your data`
            : `${daysLeft} days left in your free trial`}
        </span>
        <button
          onClick={() => navigate('/settings/billing')}
          className="font-bold underline ml-1"
          style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
        >
          Upgrade now
        </button>
      </div>
      <button onClick={handleDismiss} style={{ background: 'none', border: 'none', color: 'inherit', padding: 0 }}>
        <X size={14} />
      </button>
    </div>
  )
}
